import { InputBase, ListItemButton, ListItemIcon } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AutoSizer, List } from "react-virtualized";

import { getDataTreeUrl } from "@helpers/constants";

import TruncatedTextWithTooltip from "../../../components/TruncatedTextWithTooltip";
import { getDataFolderStream } from "../helpers/api";
import type { ClientFile, FileType } from "../helpers/utils";
import { DataContext } from "../pages/Data";
import FileIcon from "./FileIcon";
import Loading from "./Loading";
import Preview from "./Preview";

type DataColumnProps = {
  dataId: string;
  path: string;
  index: number;
  activeSegment?: string;
  type?: FileType;
  /**
   * Called with `path` once its listing shows whether it has anything: on
   * the first entries, or at the end of a listing with none.
   */
  onListed?: (path: string, hasEntries: boolean) => void;
};

const ListContainer = styled("div")({
  minWidth: "300px",
  maxWidth: "300px",
  borderRight: "1px solid lightgrey",
  display: "grid",
  gridTemplateRows: "auto 1fr",
  position: "relative",
});

const FilterContainer = styled("div")({
  borderBottom: "1px solid lightgrey",
  padding: ".25rem 1rem",
  display: "flex",
  "& .MuiInputBase-root": { width: "100%", fontSize: "1rem" },
});

const StyledListItemButton = styled(ListItemButton)({ whiteSpace: "nowrap" });

function filesReducer(files: ClientFile[], moreFiles: ClientFile[]) {
  return files.concat(moreFiles);
}

const DataColumn = (props: DataColumnProps) => {
  const navigate = useNavigate();
  const { file, setFile } = useContext(DataContext);
  const { onListed } = props;
  const [filesLoading, setFilesLoading] = useState(true);
  const [fileResponseStarted, setFileResponseStarted] = useState(false);
  const [files, addMoreFiles] = useReducer(filesReducer, []);
  const directoryName =
    file && file.kind !== "directory"
      ? props.path.replace(new RegExp(`${file.name}$`), "")
      : props.path;
  useEffect(() => {
    const dataFolderStream = getDataFolderStream(props.dataId, directoryName);
    const clientFileReader = dataFolderStream.getReader();
    let cancelled = false;
    let received = 0;

    // TODO(HOPE): See if there is a better way to do this
    (async () => {
      while (!cancelled) {
        let result: ReadableStreamReadResult<ClientFile[]>;
        try {
          result = await clientFileReader.read();
        } catch {
          // The listing failed (already logged); show what arrived, as for an
          // error response, rather than loading forever.
          if (!cancelled) {
            setFileResponseStarted(true);
            setFilesLoading(false);
            if (!received) {
              onListed?.(props.path, false);
            }
          }
          break;
        }
        const { done, value } = result;
        if (cancelled) {
          clientFileReader.releaseLock();
          dataFolderStream.cancel();
          break;
        }
        setFileResponseStarted(true);
        if (done || !value || !value.length) {
          setFilesLoading(false);
          if (!received) {
            onListed?.(props.path, false);
          }
          break;
        }
        if (!received) {
          onListed?.(props.path, true);
        }
        received += value.length;
        addMoreFiles(value);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [props.dataId, directoryName, props.path, onListed]);
  const [filter, setFilter] = useState("");
  const filterLowerCase = filter.toLowerCase();
  const list =
    files.filter((file) => file.name.toLowerCase().includes(filterLowerCase)) ||
    [];
  const activeIndex = list.findIndex((f) => f.name === props.activeSegment);
  // If we'd like to create more of a navigational component for the file directory we can remove replace:true below
  const navigateToItem = useCallback(
    (filepath: string) => {
      navigate(getDataTreeUrl(props.dataId, filepath), {
        state: { canGoBack: true },
        replace: true,
      });
    },
    [navigate, props.dataId],
  );

  useEffect(() => {
    if (props.index === 1 && files.length) {
      const file = files.find((file) => file.name === props.activeSegment);
      if (file) {
        setFile?.(`${props.path}/${file.name}`, file);
      }
    }
  }, [files, props.activeSegment, props.index, props.path, setFile]);

  if (props.index === 0 && file?.kind === "file") {
    return <Preview />;
  }

  return (
    <ListContainer className="column">
      <FilterContainer>
        <InputBase
          placeholder="Filter"
          value={filter}
          inputProps={{ "aria-label": "Filter" }}
          onChange={(e) => setFilter(e.target.value)}
        />
      </FilterContainer>
      {fileResponseStarted && (
        // Clicking the blank space below the entries goes back to this
        // column's folder, as in Finder. Without it, nothing leads back to
        // the top level (and its "Download all") once an entry there is
        // picked.
        <div
          data-testid="column-background"
          onClick={(event) => {
            if (
              props.activeSegment !== undefined &&
              !(event.target as Element).closest("[data-entry]")
            ) {
              navigateToItem(props.path);
            }
          }}
        >
          <AutoSizer>
            {({ width, height }) => (
              <List
                width={width}
                height={height}
                rowHeight={37}
                rowCount={list.length}
                scrollToIndex={activeIndex}
                rowRenderer={({ index: itemIndex, key, style }) => {
                  const item = list[itemIndex];
                  return (
                    <StyledListItemButton
                      key={key}
                      data-entry
                      style={style}
                      onClick={() =>
                        navigateToItem(
                          [props.path, item.name].filter(Boolean).join("/"),
                        )
                      }
                      selected={item.name === props.activeSegment}
                    >
                      <ListItemIcon>
                        <FileIcon type={item.type} />
                      </ListItemIcon>
                      <TruncatedTextWithTooltip
                        text={item.name}
                        placement="bottom-start"
                      />
                    </StyledListItemButton>
                  );
                }}
              />
            )}
          </AutoSizer>
        </div>
      )}
      {filesLoading && <Loading />}
    </ListContainer>
  );
};

export default DataColumn;
